import * as React from "react";
import { Box, Card, Chip, MenuItem, MenuList, Popover, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { TrashIcon } from "@phosphor-icons/react";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useNavigate } from "react-router-dom";

import { useDeleteUser, User, usersKeys } from "@/hooks/api/use-users";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { usePopover } from "@/hooks/use-popover";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { CreateUser } from "./createUser";
import { FILTER_SEPARATOR } from "@/constants";

interface AccountsChipsProps {
	accounts: Array<{ id: string; name: string }>;
}

function AccountsChips({ accounts }: AccountsChipsProps): React.JSX.Element {
	const popover = usePopover<HTMLDivElement>();
	const navigate = useNavigate();

	if (!accounts || accounts.length === 0) {
		return <Box sx={{ display: "flex", gap: 1 }} />;
	}

	const handleClick = (account: { id: string; name: string }) => {
		navigate(`/users?accountId=${account.name}${FILTER_SEPARATOR}${account.id}`);
	};

	const firstAccount = accounts[0];
	const remainingAccounts = accounts.slice(1);
	const remainingCount = remainingAccounts.length;

	return (
		<>
			<Box sx={{ display: "flex", gap: 1 }}>
				<Chip
					key={firstAccount.id}
					label={firstAccount.name}
					size="small"
					color="info"
					onClick={() => handleClick(firstAccount)}
				/>
				{remainingCount > 0 && (
					<Box ref={popover.anchorRef}>
						<Chip
							label={`+${remainingCount}`}
							size="small"
							color="info"
							variant="outlined"
							onClick={popover.handleOpen}
							sx={{ cursor: "pointer" }}
						/>
					</Box>
				)}
			</Box>
			{remainingCount > 0 && (
				<Popover
					open={popover.open}
					anchorEl={popover.anchorRef.current}
					onClose={popover.handleClose}
					anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
					transformOrigin={{ horizontal: "left", vertical: "top" }}
					slotProps={{ paper: { sx: { p: 0, maxHeight: 300, minWidth: 200 } } }}
				>
					<MenuList sx={{ px: 0.5, py: 0.5 }}>
						{accounts.slice(1).map((account) => (
							<MenuItem key={account.id} sx={{ fontSize: "14px", py: 1 }} onClick={() => handleClick(account)}>
								<Chip label={account.name} size="small" color="info" />
							</MenuItem>
						))}
					</MenuList>
				</Popover>
			)}
		</>
	);
}

export function Users(): React.JSX.Element {
	const {
		items: users,
		isLoading,
		totalItems,
		refetchData: fetchUsers,
	} = useCachedTableStateWithUrl({
		queryKey: usersKeys.lists(),
		endpoint: "/users",
	});

	const [isCreateDrawerOpen, setIsCreateDrawerOpen] = React.useState(false);
	const [selectedUser, setSelectedUser] = React.useState<User | null>(null);

	const deleteUserMutation = useDeleteUser();

	const {
		data: selectedUserToDelete,
		open: isDeleteUserModalOpen,
		handleClose: handleDeleteUserModalClose,
		handleOpen: handleDeleteUserModalOpen,
	} = useDialog<User | null>();

	const handleDeleteUser = async () => {
		if (selectedUserToDelete?.id) {
			await deleteUserMutation.mutateAsync(selectedUserToDelete.id);
			handleDeleteUserModalClose();
		}
	};

	const filtersOptions: FilterOption[] = React.useMemo(() => {
		return [
			{
				name: "accountId",
				label: "Account",
				value: "",
				withoutFetchPagination: true,
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: "/accounts",
				fetchLabel: "name",
				fetchValue: "id",
			},
		];
	}, []);

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "name",
					label: "Name",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "name",
			},
			{
				header: {
					id: "email",
					label: "Email",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "email",
			},
			{
				header: {
					id: "accounts",
					label: "Accounts",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "accounts",
				getCellValue: (row: any) => {
					return <AccountsChips accounts={row.accounts} />;
				},
			},
			{
				header: {
					id: "isSuperAdmin",
					label: "Super Admin",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "isSuperAdmin",
				getCellValue: (row: any) => {
					return (
						<Chip
							color={row.isSuperAdmin ? "primary" : "secondary"}
							variant="soft"
							size="small"
							label={row.isSuperAdmin ? "Yes" : "No"}
						/>
					);
				},
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setSelectedUser(row);
										setIsCreateDrawerOpen(true);
									},
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteUserModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, []);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Typography variant="h4">Users</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Manage users and their details
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button startIcon={<PlusIcon />} variant="contained" onClick={() => setIsCreateDrawerOpen(true)}>
									Add User
								</Button>
							</Box>
						</Stack>
					</Stack>

					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={users}
								refetchData={fetchUsers}
								isLoading={isLoading}
								totalItems={totalItems}
								filtersOptions={filtersOptions}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeleteUserModalOpen}
				onClose={handleDeleteUserModalClose}
				onDelete={handleDeleteUser}
				title="Delete User"
				description="Are you sure you want to delete this user?"
			/>

			<CreateUser
				user={selectedUser}
				isCreateDrawerOpen={isCreateDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreateDrawerOpen(false);

					setTimeout(() => {
						setSelectedUser(null);
					}, 300);
				}}
			/>
		</React.Fragment>
	);
}
